import { useQuery } from "@tanstack/react-query";
import {
  getScenarioIssuesCount,
  getScenarioIssuesGraphql,
} from "../api/services/scenarioGraphqlService";
import { useApp } from "../contexts/AppContext";
import type { ScenarioIssue } from "../types/scenarios";

export interface ScenarioIssuesResult {
  /** Issues detected for the scenario (empty in demo mode). */
  issues: ScenarioIssue[];
  /** Count of unresolved issues (server count). */
  unresolvedCount: number;
  isLoading: boolean;
  error: string | null;
  /** True when live mode is active — callers use this to decide the data source. */
  isLive: boolean;
}

const EMPTY_ISSUES: ScenarioIssue[] = [];

export const scenarioIssuesQueryKey = (scenarioId: number) =>
  ["scenarioIssues", scenarioId] as const;
export const scenarioIssuesCountQueryKey = (scenarioId: number) =>
  ["scenarioIssuesCount", scenarioId] as const;

/**
 * Live-mode data source for a scenario's Issues tab. Fetches the detected issue
 * rows and the unresolved-issue count from GraphQL (vw_ScenarioIssues).
 *
 * In demo mode this is a no-op — callers keep their existing `scenario.issues`
 * path.
 */
export function useScenarioIssues(
  scenarioId: number | undefined,
  options?: { enabled?: boolean },
): ScenarioIssuesResult {
  const { mode } = useApp();
  const isLive = mode === "live";
  const enabled =
    isLive && options?.enabled !== false && typeof scenarioId === "number";

  const listQuery = useQuery({
    queryKey: scenarioIssuesQueryKey(scenarioId ?? -1),
    queryFn: () => getScenarioIssuesGraphql(scenarioId as number),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const countQuery = useQuery({
    queryKey: scenarioIssuesCountQueryKey(scenarioId ?? -1),
    queryFn: () => getScenarioIssuesCount(scenarioId as number),
    enabled,
    staleTime: 30_000,
  });

  // Stable empty reference so consumers depending on `issues` don't loop while
  // the query has no data yet.
  const issues = listQuery.data ?? EMPTY_ISSUES;
  const unresolvedCount =
    countQuery.data ?? issues.filter((i) => !i.resolution).length;

  return {
    issues,
    unresolvedCount,
    isLoading: enabled && (listQuery.isPending || countQuery.isPending),
    error:
      listQuery.error instanceof Error
        ? listQuery.error.message
        : listQuery.error
          ? "Failed to load scenario issues"
          : null,
    isLive,
  };
}
